import React, { useMemo, useCallback } from 'react';
import {
  Loader2, Calendar, MapPin, Share2, FileText, ArrowRight, Zap,
  Cpu, Package, AlertTriangle, CheckCircle2, Clock, Map as MapIcon,
} from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { useGetComponentListQuery, type ComponentListItem } from '../../redux/api/entityApi';
import { useGetSiteArtefactsQuery, useGetSiteMetadataQuery, type SiteMetadata } from '../../redux/api/portfolioApi';
import { useGetSpatialLayersQuery } from '../../redux/api/spatialApi';
import { useListSiteEventsQuery } from '../../redux/api/eventsApi';
import { useGetTaskStatisticsQuery } from '../../redux/api/tasksApi';
import { SEVERITY_HEX_COLORS } from '../../constants/eventStyles';
import EventTimeline from '../events/EventTimeline';

const SiteDetailsTab: React.FC = () => {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id || '';
  const tenantId = currentSite?.tenant_id || '';
  const portfolioId = currentSite?.portfolio_id || '';

  const { data: componentsData, isLoading: componentsLoading } = useGetComponentListQuery(
    { portfolio_id: portfolioId, site_id: siteId },
    { skip: !portfolioId || !siteId }
  );

  const { data: eventsData, isLoading: eventsLoading } = useListSiteEventsQuery(
    { siteId, limit: 100 },
    { skip: !siteId }
  );

  const { data: taskStats } = useGetTaskStatisticsQuery(
    { siteId },
    { skip: !siteId }
  );

  const { data: metadataData } = useGetSiteMetadataQuery(
    { portfolio_id: portfolioId, site_id: siteId },
    { skip: !portfolioId || !siteId }
  );

  const { data: artefactsData } = useGetSiteArtefactsQuery(
    { tenant_id: tenantId, site_id: siteId, limit: 100 },
    { skip: !siteId || !tenantId }
  );

  const { data: spatialData } = useGetSpatialLayersQuery(
    siteId,
    { skip: !siteId }
  );

  const events = eventsData?.items || [];
  const components = componentsData?.items || [];
  const artefacts = artefactsData?.items || [];

  const componentSummary = useMemo(() => {
    const groups: Record<string, { count: number; models: Set<string> }> = {};
    components.forEach((c) => {
      const type = c.component_type || 'Unknown';
      if (!groups[type]) groups[type] = { count: 0, models: new Set() };
      groups[type].count++;
      const model = (c.properties as any)?.model || (c.properties as any)?.manufacturer;
      if (model) groups[type].models.add(String(model));
    });
    return Object.entries(groups).sort((a, b) => b[1].count - a[1].count);
  }, [components]);

  const severityCounts = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0, unresolved: 0 };
    events.forEach((e) => {
      if (e.status !== 'resolved' && e.status !== 'closed') counts.unresolved++;
      counts[e.severity as keyof typeof counts] = (counts[e.severity as keyof typeof counts] || 0) + 1;
    });
    return counts;
  }, [events]);

  const docSummary = useMemo(() => {
    const groups: Record<string, { count: number; processed: number }> = {};
    artefacts.forEach((a) => {
      const type = a.doc_type || 'Unknown';
      if (!groups[type]) groups[type] = { count: 0, processed: 0 };
      groups[type].count++;
      if (a.is_processed) groups[type].processed++;
    });
    return Object.entries(groups);
  }, [artefacts]);

  const siteMetadata = useMemo(() => {
    if (!metadataData) return null;
    return metadataData as SiteMetadata;
  }, [metadataData]);

  const siteLocation = useMemo(() => {
    if (!metadataData) return null;
    const meta = metadataData as any;
    const lat = meta?.latitude || meta?.site_lat_lon?.[0];
    const lon = meta?.longitude || meta?.site_lat_lon?.[1];
    if (lat && lon) return { lat: Number(lat), lon: Number(lon) };
    return null;
  }, [metadataData]);

  const openTab = useCallback((tabType: string) => {
    window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType } }));
  }, []);

  if (!siteId) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--color-text-muted)]">
        Select a site to view details
      </div>
    );
  }

  const siteName = currentSite?.site_name || currentSite?.name || 'Unknown Site';
  const capacity = currentSite?.site_capacity_mw || currentSite?.capacity_mw;

  return (
    <div className="eg-page-shell">
      {/* Page Header */}
      <div className="eg-page-header">
        <div className="eg-page-header-glow" />
        <div className="px-6 py-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" title="Active" />
                <h1 className="eg-page-header-title text-xl font-bold">{siteName}</h1>
                {currentSite?.site_code && (
                  <span
                    className="px-2 py-0.5 text-[10px] font-mono font-medium rounded-md"
                    style={{ background: 'var(--color-dash-header-surface)', color: 'var(--color-dash-header-secondary)' }}
                  >
                    {currentSite.site_code}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs flex-wrap" style={{ color: 'var(--color-dash-header-secondary)' }}>
                {currentSite?.location && (
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {currentSite.location}</span>
                )}
                {currentSite?.site_type && (
                  <span className="flex items-center gap-1">{currentSite.site_type}</span>
                )}
                {currentSite?.commissioning_date && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Commissioned {new Date(currentSite.commissioning_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}
                  </span>
                )}
                {currentSite?.timezone && (
                  <span style={{ color: 'var(--color-dash-header-muted)' }}>{currentSite.timezone}</span>
                )}
              </div>
            </div>
          </div>

          {/* Clickable metric pills */}
          <div className="grid grid-cols-4 gap-3 mt-4">
            <MetricPill
              icon={<Zap className="w-4 h-4 text-yellow-500" />}
              label="Capacity"
              value={capacity ? `${capacity} MW` : '\u2014'}
            />
            <MetricPill
              icon={<Package className="w-4 h-4 text-[#17c480]" />}
              label="Components"
              value={components.length}
              onClick={() => openTab('componentlist')}
            />
            <MetricPill
              icon={<Calendar className="w-4 h-4 text-blue-500" />}
              label="Events"
              value={events.length}
              onClick={() => openTab('eventstasks')}
              alert={severityCounts.unresolved > 0 ? `${severityCounts.unresolved} unresolved` : undefined}
            />
            <MetricPill
              icon={<FileText className="w-4 h-4 text-purple-500" />}
              label="Documents"
              value={artefacts.length}
              onClick={() => openTab('documents')}
            />
          </div>
        </div>
      </div>

      <div className="eg-page-accent-divider" />

      {/* Content Zone */}
      <div className="eg-page-content">
        <div className="px-6 py-5 space-y-4">
          {/* Row 2: Tech Specs + Components + Events */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Technical Specs Card */}
            <div className="eg-card-elevated p-4">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2 mb-3">
                <Cpu className="w-4 h-4 text-[#17c480]" />
                Technical Specs
              </h2>
              <TechSpecsContent metadata={siteMetadata} location={siteLocation} />
            </div>

            {/* Component Summary Card */}
            <div className="eg-card-elevated p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#17c480]" />
                  Components
                </h2>
                <button onClick={() => openTab('componentlist')} className="text-xs text-[#17c480] hover:underline flex items-center gap-1">
                  View All <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {componentsLoading ? (
                <LoadingState />
              ) : components.length === 0 ? (
                <EmptyState text="No components found" />
              ) : (
                <>
                  {/* Total + mini donut */}
                  <div className="flex items-center gap-3 mb-3">
                    <MiniDonut data={componentSummary.slice(0, 4)} />
                    <div>
                      <p className="text-2xl font-bold text-[var(--color-text-primary)]">{components.length}</p>
                      <p className="text-[10px] text-[var(--color-text-muted)]">{componentSummary.length} types</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {componentSummary.slice(0, 5).map(([type, data], i) => (
                      <div key={type} className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                        <span className="text-[var(--color-text-primary)] flex-1 truncate">{type}</span>
                        {data.models.size > 0 && (
                          <span className="text-[var(--color-text-muted)] truncate max-w-[80px] text-[10px]">
                            {Array.from(data.models)[0]}
                          </span>
                        )}
                        <span className="font-medium text-[var(--color-text-secondary)] min-w-[24px] text-right">{data.count}</span>
                      </div>
                    ))}
                    {componentSummary.length > 5 && (
                      <p className="text-[10px] text-[var(--color-text-muted)]">+{componentSummary.length - 5} more types</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Events Card */}
            <div className="eg-card-elevated p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#17c480]" />
                  Events
                </h2>
                <button onClick={() => openTab('eventstasks')} className="text-xs text-[#17c480] hover:underline flex items-center gap-1">
                  View All <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {eventsLoading ? (
                <LoadingState />
              ) : events.length === 0 ? (
                <EmptyState text="No events recorded" />
              ) : (
                <>
                  <EventTimeline events={events.slice(0, 50)} height={50} />

                  {/* Severity bar */}
                  <div className="mt-3">
                    <SeverityBar counts={severityCounts} total={events.length} />
                  </div>

                  {/* Unresolved alert */}
                  {severityCounts.unresolved > 0 && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs">
                      <AlertTriangle className="w-3 h-3 text-orange-400" />
                      <span className="text-orange-500 font-medium">{severityCounts.unresolved} unresolved</span>
                    </div>
                  )}

                  {/* Stats row */}
                  <div className="flex gap-3 mt-2 text-xs text-[var(--color-text-muted)]">
                    <span>{events.length} total</span>
                    {taskStats && (
                      <>
                        <span>{taskStats.total} tasks</span>
                        {taskStats.overdue > 0 && <span className="text-red-500">{taskStats.overdue} overdue</span>}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Row 3: Site Map + Network + Documents */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Site Map Card */}
            <div className="eg-card-elevated p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
                  <MapIcon className="w-4 h-4 text-[#17c480]" />
                  Site Map
                </h2>
                <button
                  onClick={() => openTab('sitemap')}
                  className="px-3 py-1 text-xs font-medium rounded-md bg-[#17c480]/10 text-[#17c480] hover:bg-[#17c480]/20 transition-colors flex items-center gap-1"
                >
                  Open Map <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {spatialData && spatialData.layers.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-2xl font-bold text-[var(--color-text-primary)]">
                    {spatialData.layers.reduce((sum, l) => sum + l.count, 0).toLocaleString()}
                  </p>
                  <p className="text-[10px] text-[var(--color-text-muted)] mb-2">
                    features across {spatialData.layers.length} layers
                  </p>
                  <div className="space-y-1.5">
                    {spatialData.layers
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 5)
                      .map((layer) => (
                        <div key={layer.type} className="flex items-center gap-2 text-xs">
                          <span className="text-[var(--color-text-primary)] flex-1 truncate">
                            {layer.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                          {layer.has_entities && (
                            <span className="text-[9px] text-[#17c480] font-medium">linked</span>
                          )}
                          <span className="font-medium text-[var(--color-text-secondary)] min-w-[40px] text-right tabular-nums">
                            {layer.count >= 1000 ? `${(layer.count / 1000).toFixed(1)}k` : layer.count}
                          </span>
                        </div>
                      ))}
                    {spatialData.layers.length > 5 && (
                      <p className="text-[10px] text-[var(--color-text-muted)]">+{spatialData.layers.length - 5} more layers</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="py-4">
                  <MapPin className="w-6 h-6 text-[var(--color-text-tertiary)] mx-auto mb-2" />
                  <p className="text-xs text-[var(--color-text-muted)] text-center">No spatial data uploaded</p>
                  <p className="text-[10px] text-[var(--color-text-tertiary)] text-center mt-1">
                    Upload GeoJSON to visualize site layout
                  </p>
                </div>
              )}
            </div>

            {/* Network Card */}
            <div className="eg-card-elevated p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-[#17c480]" />
                  Network
                </h2>
                <button
                  onClick={() => openTab('networkviewer')}
                  className="px-3 py-1 text-xs font-medium rounded-md bg-[#17c480]/10 text-[#17c480] hover:bg-[#17c480]/20 transition-colors flex items-center gap-1"
                >
                  Open Network <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {components.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-2xl font-bold text-[var(--color-text-primary)]">{components.length}</p>
                  <p className="text-[10px] text-[var(--color-text-muted)] mb-2">components in network</p>
                  <div className="grid grid-cols-3 gap-2">
                    {componentSummary.slice(0, 3).map(([type, data], i) => (
                      <div key={type} className="p-2 rounded-lg bg-[var(--color-bg-tertiary)]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                          <span className="text-[10px] font-medium text-[var(--color-text-primary)] truncate">{type}</span>
                        </div>
                        <p className="text-sm font-bold text-[var(--color-text-primary)]">{data.count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <EmptyState text="No network data" />
              )}
            </div>

            {/* Documents Card */}
            <div className="eg-card-elevated p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#17c480]" />
                  Documents
                </h2>
                <button onClick={() => openTab('documents')} className="text-xs text-[#17c480] hover:underline flex items-center gap-1">
                  View All <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {artefacts.length === 0 ? (
                <EmptyState text="No documents uploaded" />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {docSummary.map(([type, data]) => {
                    const pct = data.count > 0 ? (data.processed / data.count) * 100 : 0;
                    return (
                      <div key={type} className="p-2.5 rounded-lg bg-[var(--color-bg-tertiary)]" style={{ boxShadow: 'var(--shadow-sm)' }}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <FileText className="w-3 h-3 text-[var(--color-text-muted)] shrink-0" />
                          <p className="text-xs font-medium text-[var(--color-text-primary)] truncate">{type}</p>
                        </div>
                        <p className="text-sm font-bold text-[var(--color-text-primary)]">{data.count}</p>
                        {data.count > 0 && (
                          <div className="mt-1.5">
                            <div className="flex items-center justify-between text-[9px] text-[var(--color-text-muted)] mb-0.5">
                              <span>{data.processed}/{data.count} processed</span>
                            </div>
                            <div className="h-1 bg-[var(--color-border)] rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : 'bg-[#17c480]'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Sub-components ---

function MetricPill({ icon, label, value, onClick, alert }: {
  icon: React.ReactNode; label: string; value: string | number; onClick?: () => void; alert?: string;
}) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={`px-4 py-3 flex items-center gap-3 rounded-lg ${onClick ? 'cursor-pointer hover:opacity-80' : ''} transition-colors`}
      style={{ background: 'var(--color-dash-header-surface)' }}
    >
      <div className="p-1.5 rounded-md" style={{ background: 'var(--color-dash-header-surface)' }}>{icon}</div>
      <div>
        <p className="text-[10px] leading-tight" style={{ color: 'var(--color-dash-header-muted)' }}>{label}</p>
        <p className="text-sm font-bold" style={{ color: 'var(--color-dash-header-text)' }}>{value}</p>
        {alert && <p className="text-[9px] text-orange-500 mt-0.5">{alert}</p>}
      </div>
    </Wrapper>
  );
}

function TechSpecsContent({ metadata, location }: { metadata: SiteMetadata | null; location: { lat: number; lon: number } | null }) {
  if (!metadata && !location) {
    return <p className="text-xs text-[var(--color-text-muted)] py-4">No technical data available</p>;
  }

  const specs: { label: string; value: string }[] = [];
  if (metadata) {
    if (metadata.ac_capacity_kw) specs.push({ label: 'AC Capacity', value: `${metadata.ac_capacity_kw.toLocaleString()} kW` });
    if (metadata.dc_capacity_kw) specs.push({ label: 'DC Capacity', value: `${metadata.dc_capacity_kw.toLocaleString()} kW` });
    if (metadata.number_of_modules) specs.push({ label: 'Modules', value: metadata.number_of_modules.toLocaleString() });
    if (metadata.number_of_inverters) specs.push({ label: 'Inverters', value: metadata.number_of_inverters.toLocaleString() });
    if (metadata.modules_per_string) specs.push({ label: 'Modules/String', value: metadata.modules_per_string.toLocaleString() });
    if (metadata.number_of_strings) specs.push({ label: 'Strings', value: metadata.number_of_strings.toLocaleString() });
    if (metadata.tilt != null) specs.push({ label: 'Tilt', value: `${metadata.tilt}\u00B0` });
    if (metadata.azimuth != null) specs.push({ label: 'Azimuth', value: `${metadata.azimuth}\u00B0` });
  }

  return (
    <div className="space-y-3">
      {specs.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {specs.map(({ label, value }) => (
            <div key={label}>
              <p className="text-[10px] text-[var(--color-text-muted)]">{label}</p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">{value}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-[var(--color-text-muted)]">No specifications available</p>
      )}

      {location && (
        <div className="pt-2 border-t border-[var(--color-border)]">
          <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
            <MapPin className="w-3 h-3 text-[#17c480]" />
            <span>{location.lat.toFixed(6)}, {location.lon.toFixed(6)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const DONUT_COLORS = ['#17c480', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899'];

function MiniDonut({ data }: { data: [string, { count: number; models: Set<string> }][] }) {
  const total = data.reduce((sum, [, d]) => sum + d.count, 0);
  if (total === 0) return null;

  const size = 48;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <svg width={size} height={size} className="shrink-0">
      {data.map(([type, d], i) => {
        const pct = d.count / total;
        const dashArray = `${pct * circumference} ${circumference}`;
        const rotation = offset * 360 - 90;
        offset += pct;
        return (
          <circle
            key={type}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray}
            strokeDashoffset={0}
            transform={`rotate(${rotation} ${size / 2} ${size / 2})`}
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

function SeverityBar({ counts, total }: { counts: { critical: number; high: number; medium: number; low: number }; total: number }) {
  if (total === 0) return null;
  const segments = [
    { key: 'critical', count: counts.critical, color: SEVERITY_HEX_COLORS.critical },
    { key: 'high', count: counts.high, color: SEVERITY_HEX_COLORS.high },
    { key: 'medium', count: counts.medium, color: SEVERITY_HEX_COLORS.medium },
    { key: 'low', count: counts.low, color: SEVERITY_HEX_COLORS.low },
  ].filter(s => s.count > 0);

  return (
    <div>
      <div className="flex h-2 rounded-full overflow-hidden gap-px">
        {segments.map(({ key, count, color }) => (
          <div
            key={key}
            className="h-full rounded-full first:rounded-l-full last:rounded-r-full"
            style={{ width: `${(count / total) * 100}%`, backgroundColor: color }}
            title={`${key}: ${count}`}
          />
        ))}
      </div>
      <div className="flex gap-3 mt-1.5 text-[10px] text-[var(--color-text-muted)]">
        {segments.map(({ key, count, color }) => (
          <span key={key} className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
            {count} {key}
          </span>
        ))}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)] py-4">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p className="text-xs text-[var(--color-text-muted)] py-4">{text}</p>;
}

export default SiteDetailsTab;
